/* eslint-disable no-useless-catch */
import { openDB } from 'idb';
import { appConfig } from '../config/app-config';
import { CompanionEntity } from '../common/entity';
import {
  CursorAccess,
  INVOICE_SUMMARY_INDEX,
  MIS_COMPLIANCE_INDEX,
  MIS_METADATA_INDEX,
  MIS_SUMMARY_INDEX,
  QueryType,
} from './constants';
import { Query } from './types';

export const dbPromise = openDB(appConfig.IDB.NAME, appConfig.IDB.VERSION, {
  async upgrade(db, oldVersion, newVersion, transaction) {
    db.createObjectStore(appConfig.IDB.STORE.MIS_SUMMARY, {
      keyPath: 'id',
      autoIncrement: true,
    });
    const summaryStore = transaction.objectStore(
      appConfig.IDB.STORE.MIS_SUMMARY
    );
    summaryStore.createIndex(
      MIS_SUMMARY_INDEX.ORG_ID_INDEX.NAME,
      MIS_SUMMARY_INDEX.ORG_ID_INDEX.KEY
    );

    db.createObjectStore(appConfig.IDB.STORE.MIS_METADATA, {
      keyPath: 'id',
      autoIncrement: true,
    });
    const metadataStore = transaction.objectStore(
      appConfig.IDB.STORE.MIS_METADATA
    );
    metadataStore.createIndex(
      MIS_METADATA_INDEX.DATE_INDEX.NAME,
      MIS_METADATA_INDEX.DATE_INDEX.KEY
    );

    db.createObjectStore(appConfig.IDB.STORE.MIS_COMPLIANCE, {
      keyPath: 'id',
      autoIncrement: true,
    });
    const complianceStore = transaction.objectStore(
      appConfig.IDB.STORE.MIS_COMPLIANCE
    );
    complianceStore.createIndex(
      MIS_COMPLIANCE_INDEX.ORG_ID_INDEX.NAME,
      MIS_COMPLIANCE_INDEX.ORG_ID_INDEX.KEY
    );

    db.createObjectStore(appConfig.IDB.STORE.INVOICE_SUMMARY, {
      keyPath: 'id',
      autoIncrement: true,
    });
    const invoiceStore = transaction.objectStore(
      appConfig.IDB.STORE.INVOICE_SUMMARY
    );
    invoiceStore.createIndex(
      INVOICE_SUMMARY_INDEX.ORG_ID_DATE_INDEX.NAME,
      INVOICE_SUMMARY_INDEX.ORG_ID_DATE_INDEX.KEY
    );
    invoiceStore.createIndex(
      INVOICE_SUMMARY_INDEX.ORG_ID_INVOICE_ID_INDEX.NAME,
      INVOICE_SUMMARY_INDEX.ORG_ID_INVOICE_ID_INDEX.KEY
    );
  },
});

export const clearDb = async () => {
  try {
    const clearStore = async (store: string) => {
      const tx = (await dbPromise).transaction(store, CursorAccess.READ_WRITE);
      await Promise.all([tx.store.clear(), tx.done]);
    };

    await clearStore(appConfig.IDB.STORE.MIS_SUMMARY);
    await clearStore(appConfig.IDB.STORE.INVOICE_SUMMARY);
    await clearStore(appConfig.IDB.STORE.MIS_COMPLIANCE);
    await clearStore(appConfig.IDB.STORE.MIS_METADATA);
  } catch (e) {
    // console.log('Failed to clear idb');
  }
};

export const writeData = async (store: string, data: CompanionEntity) => {
  try {
    const tx = (await dbPromise).transaction(store, 'readwrite');
    await Promise.all([tx.store.put(data), tx.done]);
  } catch (error) {
    // console.log('Failed to write data in idb', error);
    throw error;
  }
};

export const readData = async (store: string, key: string) => {
  try {
    const tx = (await dbPromise).transaction(store, 'readonly');
    await Promise.all([tx.store.get(key), tx.done]);
  } catch (error) {
   //  console.log('Failed to read data from idb', error);
    throw error;
  }
};

export const readDataWithIndex = async (
  store: string,
  key: string[],
  index: string
): Promise<CompanionEntity> => {
  try {
    const tx = (await dbPromise).transaction(store, 'readonly');
    const st = tx.store.index(index);
    const data = await st.get(key);
    await tx.done;
    return data;
  } catch (error) {
    // console.log('Failed to read data from idb', error);
    throw error;
  }
};

export const getCursor = async (
  store: string,
  index?: string,
  query?: Query,
  access?: CursorAccess,
  queryType?: QueryType
) => {
  let cursor;
  if (index) {
    const idx = (await dbPromise).transaction(store, access).store.index(index);
    let range;
    if (query && queryType === QueryType.ONLY) {
      range = IDBKeyRange.only(query);
    } else if (query && queryType === QueryType.BOUND) {
      // @ts-ignore
      range = IDBKeyRange.bound(query.lowerBound, query.upperBound);
    } else {
      cursor = await idx.openCursor();
      return cursor;
    }
    cursor = await idx.openCursor(range);
    return cursor;
  }
  cursor = (await dbPromise).transaction(store).store.openCursor();
  return cursor;
};
